import React from 'react';
import {
  Dashboard as DashboardIcon,
  Star as StarIcon,
  Psychology as PsychologyIcon,
  ChatBubbleOutline as ChatBubbleIcon,
  StickyNote2 as StickyNoteIcon,
  School as SchoolIcon
} from '@mui/icons-material';
import LanguageSwitcher from '../../components/LanguageSwitcher';

/**
 * 创建学生侧边栏配置
 * 可以在多个页面中复用
 * @param {Function} t - 翻译函数，从 useTranslation() 获取，必须传入
 */
export function createStudentSidebarConfig({
  navigate,
  user,
  selectedCourse,
  onLogout,
  t, // 翻译函数，必须传入
  // 可选的状态和回调
  categories = [],
  selectedCategory = 'all',
  onCategoryChange,
  showBookmarks = false,
  showMemory = false,
  setShowBookmarks,
  setShowMemory,
  setSelectedCategory,
  // 当前路径，用于高亮
  currentPath = '/dashboard'
}) {
  const isActive = (path) => {
    return currentPath === path || currentPath.startsWith(path + '/');
  };

  return {
    courseName: selectedCourse?.name,
    courseTermCode: selectedCourse?.termCode,
    userInfo: { 
      name: user?.name, 
      role: t('dashboard.student.studentAccount'),
      avatar: user?.avatar 
    },
    onLogout,
    footerContent: <LanguageSwitcher />,
    navigationGroups: [
      {
        subHeading: t('dashboard.student.navigation', { defaultValue: '导航' }),
        items: [
          {
            icon: <SchoolIcon color={isActive('/courses') ? 'primary' : 'inherit'} />,
            label: t('dashboard.student.courses', { defaultValue: '课程' }),
            selected: isActive('/courses'),
            onClick: () => {
              if (setShowBookmarks) setShowBookmarks(false);
              if (setShowMemory) setShowMemory(false);
              if (setSelectedCategory) setSelectedCategory('all');
              navigate('/courses');
            }
          }
        ]
      },
      {
        subHeading: t('dashboard.student.learning', { defaultValue: '学习' }),
        items: [
          {
            icon: <DashboardIcon color={!showBookmarks && !showMemory && selectedCategory === 'all' && isActive('/questions') ? 'primary' : 'inherit'} />,
            label: t('dashboard.student.overview'),
            selected: !showBookmarks && !showMemory && selectedCategory === 'all' && isActive('/questions'),
            onClick: () => {
              if (setShowBookmarks) setShowBookmarks(false);
              if (setShowMemory) setShowMemory(false);
              if (setSelectedCategory) setSelectedCategory('all');
              navigate('/questions');
            }
          },
          {
            icon: <StickyNoteIcon color={isActive('/notebooks') ? 'primary' : 'inherit'} />,
            label: t('dashboard.student.courseNotes'),
            selected: isActive('/notebooks'),
            onClick: () => {
              if (setShowBookmarks) setShowBookmarks(false);
              if (setShowMemory) setShowMemory(false);
              if (setSelectedCategory) setSelectedCategory('all');
              navigate('/notebooks');
            }
          },
          {
            icon: <StarIcon color={showBookmarks || isActive('/bookmarks') ? 'primary' : 'inherit'} />,
            label: t('dashboard.student.bookmarks'),
            selected: showBookmarks || isActive('/bookmarks'),
            onClick: () => {
              if (setShowBookmarks) {
                setShowBookmarks(true);
                if (setShowMemory) setShowMemory(false);
                if (setSelectedCategory) setSelectedCategory('all');
              } else {
                navigate('/bookmarks');
              }
            }
          }
        ]
      },
      {
        subHeading: t('dashboard.student.other', { defaultValue: '其他' }),
        items: [
          {
            icon: <PsychologyIcon color={(showMemory || isActive('/dashboard')) && currentPath.includes('memory') ? 'primary' : 'inherit'} />,
            label: t('dashboard.student.chatHistory'),
            selected: showMemory || (isActive('/dashboard') && currentPath.includes('memory')),
            onClick: () => {
              // Always navigate to /memory route instead of using state management
              navigate('/memory');
            }
          },
          {
            icon: <ChatBubbleIcon color={isActive('/forum') ? 'primary' : 'inherit'} />,
            label: t('dashboard.student.courseForum'),
            selected: isActive('/forum'),
            onClick: () => {
              if (setShowBookmarks) setShowBookmarks(false);
              if (setShowMemory) setShowMemory(false);
              if (setSelectedCategory) setSelectedCategory('all');
              navigate('/forum');
            }
          }
        ]
      }
    ],
    navigationItems: [] // Keep for backward compatibility
  };
}
